import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useParams, useHistory } from 'react-router-dom'
import { sendErrorReport, displayValue } from 'shared/helpers'
import { Button, IconDelete, ConfirmationPopup, List } from 'shared/components'
import {
  getCustomerAccLicenseManagers,
  removeManagerFromCustomerAccount
} from 'src/customer/actions'
import AddLicenseManager from './AddLicenseManager'
import './styles.scss'

const LicenseManagers = () => {
  const history = useHistory()
  const { accountId, companyId } = useParams()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [managersLoading, setManagersLoading] = useState(true)
  const [managers, setManagers] = useState([])
  const [managersCount, setManagersCount] = useState(0)
  const [isAddManagerModalOpen, setAddManagerModalOpen] = useState(false)
  const [isDeleteManagerPopupDisplayed, setIsDeleteManagerPopupDisplayed] = useState(false)
  const [managerToDelete, setManagerToDelete] = useState(null)
  const [deletingManager, setDeletingManager] = useState(false)

  const fetchManagers = (companyId, accountId, tablePage = page, rows = rowsPerPage) => {
    getCustomerAccLicenseManagers(companyId, accountId, tablePage, rows)
      .then(res => {
        setManagers(get(res, 'data') || [])
        setManagersCount(get(res, 'data.count'))
        setManagersLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot fetch managers list')
        setManagersLoading(false)
      })
  }

  useEffect(() => {
    setManagersLoading(true)
    fetchManagers(companyId, accountId, page, rowsPerPage)
  }, [companyId, accountId, page, rowsPerPage])

  const redirectToLicenseManagerPage = rowData => {
    const licenseManagerID = get(rowData, 'original.id')
    history.push(`/${companyId}/license-managers/${licenseManagerID}`)
  }

  const submitOrdersManipulation = value => {
    if (value === 'refreshManagers') fetchManagers(companyId, accountId, page, rowsPerPage)
    setAddManagerModalOpen(false)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    fetchManagers(companyId, accountId, newPage, rowsPerPage)
  }

  const handlePageSizeChange = (newPageSize, newPage) => {
    setPage(newPage)
    setRowsPerPage(newPageSize)
    fetchManagers(companyId, accountId, newPage, newPageSize)
  }

  return (
    <>
      <Button theme='default' size='sm' onClick={() => setAddManagerModalOpen(true)}>
        {__('Add manager')}
      </Button>
      {isAddManagerModalOpen && (
        <AddLicenseManager
          closeCb={() => setAddManagerModalOpen(false)}
          confirmCb={submitOrdersManipulation}
        />
      )}
      <List
        clickable
        data={managers || []}
        loading={managersLoading}
        minRows={2}
        handleClick={rowData => redirectToLicenseManagerPage(rowData)}
        showPageSizeOptions
        pages={Math.ceil(managersCount / rowsPerPage)}
        page={page}
        onPageChange={newPage => handlePageChange(newPage)}
        pageSize={rowsPerPage}
        onPageSizeChange={(pageSize, pageIndex) => handlePageSizeChange(pageSize, pageIndex)}
        columns={[
          {
            accessor: 'true_email',
            Header: __('Email'),
            Cell: rowData => displayValue(rowData.value),
            minWidth: 250,
            width: 350
          },
          {
            accessor: 'first_name',
            Header: __('First Name'),
            Cell: rowData => displayValue(rowData.value),
            maxWidth: 250
          },
          {
            accessor: 'last_name',
            Header: __('Last Name'),
            Cell: rowData => displayValue(rowData.value),
            maxWidth: 250
          },
          {
            accessor: 'phone_number',
            Header: __('Phone Number'),
            Cell: rowData => displayValue(rowData.value),
            maxWidth: 250
          },
          {
            className: 'text-center',
            id: 'delete',
            sortable: false,
            Cell: rowData => (
              <Button
                className='edit-button'
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setManagerToDelete(rowData.original)
                  setIsDeleteManagerPopupDisplayed(true)
                }}
                type='button'
              >
                <IconDelete height='16px' width='16px' color='#ee5253' />
              </Button>
            )
          }
        ]}
      />
      {isDeleteManagerPopupDisplayed && (
        <ConfirmationPopup
          title={__('Delete manager')}
          text={__('Are you sure you want to delete this manager?')}
          confirmCb={() => {
            setDeletingManager(true)
            removeManagerFromCustomerAccount(companyId, accountId, {
              license_user_id: managerToDelete.id
            })
              .then(() => {
                setIsDeleteManagerPopupDisplayed(false)
                fetchManagers(companyId, accountId, page, rowsPerPage)
              })
              .catch(err => {
                sendErrorReport(err, 'Cannot delete user group manager')
              })
              .finally(() => {
                setDeletingManager(false)
              })
          }}
          closeCb={() => setIsDeleteManagerPopupDisplayed(false)}
          disabled={deletingManager}
        >
          <div>
            <strong>{get(managerToDelete, 'true_email')}</strong>
          </div>
        </ConfirmationPopup>
      )}
    </>
  )
}

export default LicenseManagers
